import { useState, useEffect, useRef, useMemo } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ChartCard from "../../../common/components/ChartCard";
import { Sort, Download } from "@mui/icons-material";
import DistrictVacancyDistributionChart from "./charts/DistrictVacancyDistributionChart";
import QualificationVacanciesChart from "./charts/QualificationVacanciesChart";
import PositionVacanciesChart from "./charts/PositionVacanciesChart";
import TeachingLevelVacanciesChart from "./charts/TeachingLevelVacanciesChart";
import AcademicYearVacancyDistributionChart from "./charts/AcademicYearVacancyDistributionChart";
import OversightSkeleton from "../../../common/components/OversightSkeleton";
import LayoutControl from "../common/LayoutControl";
import VacancyDistributionTable from "./charts/VacancyDistributionTable";

const VacanciesOversight = (props) => {
  const { user, loading } = props;

  const [isLoading1, setIsLoading1] = useState(true);
  const [data, setData] = useState({});
  const [layout, setLayout] = useState("default");
  const [exportLoading, setExportLoading] = useState({ image: false, pdf: false });
  const isNotGlobal = user?.selectedEntity?.id !== "00000000";
  const [vacancyDistributionSortBy, setVacancyDistributionSortBy] =
    useState("district");
  const vacancyDistributionTableRef = useRef(null);
  const contentRef = useRef(null);

  const handleVacancyDistributionExport = () => {
    if (
      vacancyDistributionTableRef.current &&
      vacancyDistributionTableRef.current.exportToExcel
    ) {
      vacancyDistributionTableRef.current.exportToExcel();
    }
  };

  const handleExportImage = async () => {
    try {
      setExportLoading(prev => ({ ...prev, image: true }));
      const scale = 2;
      const canvas = await html2canvas(contentRef.current, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        imageTimeout: 0,
        quality: 1.0,
        height: contentRef.current.scrollHeight,
        width: contentRef.current.scrollWidth,
      });

      const link = document.createElement("a");
      link.download = `Vacancies-Overview-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL("image/png", 1.0);
      link.click();
    } catch (error) {
      toast.error("Failed to export image. Please try again.");
    } finally {
      setExportLoading(prev => ({ ...prev, image: false }));
    }
  };

  const handleExportPDF = async () => {
    try {
      setExportLoading(prev => ({ ...prev, pdf: true }));
      const scale = 2;
      const canvas = await html2canvas(contentRef.current, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        imageTimeout: 0,
        quality: 1.0,
        height: contentRef.current.scrollHeight,
        width: contentRef.current.scrollWidth,
      });

      const imgData = canvas.toDataURL("image/png", 1.0);
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? "l" : "p",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);

      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = (pdfHeight - imgHeight * ratio) / 2;

      pdf.addImage(
        imgData,
        "PNG",
        imgX,
        imgY,
        imgWidth * ratio,
        imgHeight * ratio,
        "",
        "FAST"
      );
      pdf.save(`Vacancies-Overview-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      toast.error("Failed to export PDF. Please try again.");
    } finally {
      setExportLoading(prev => ({ ...prev, pdf: false }));
    }
  };

  useEffect(() => {
    if (user?.selectedEntity) {
      getVacanciesOversights();
    }
  }, [user?.selectedEntity]);

  const getVacanciesOversights = async () => {
    try {
      setIsLoading1(true);
      const { data } = await axios.get(`/api/oversight/vacancies`);

      setData(data);
      setIsLoading1(false);
    } catch (error) {
      setIsLoading1(false);
    }
  };

  const staffDistributionCustomActions = useMemo(() => {
    return [
      {
        label: "Sort by",
        icon: <Sort />,
        submenu: [
          {
            label: "District (A-Z)",
            onClick: () => setVacancyDistributionSortBy("district"),
            selected: vacancyDistributionSortBy === "district",
          },
          {
            label: "Total Vacancies (High-Low)",
            onClick: () => setVacancyDistributionSortBy("total"),
            selected: vacancyDistributionSortBy === "total",
          },
        ],
      },
      {
        label: "Export to Excel",
        icon: <Download />,
        onClick: handleVacancyDistributionExport,
      },
    ];
  }, [vacancyDistributionSortBy]);

  const getLayoutClass = (index) => {
    switch (layout) {
      case "leftWide":
        return index % 2 === 0 ? "col-12 col-md-8 p-0" : "col-12 col-md-4 p-0";
      case "rightWide":
        return index % 2 === 0 ? "col-12 col-md-4 p-0" : "col-12 col-md-8 p-0";
      case "full":
        return "col-12 p-0";
      default:
        return "col-12 col-md-6 p-0";
    }
  };

  if (isLoading1 || loading) {
    return <OversightSkeleton />;
  }

  return (
    <div className="pl-4 pt-2">
      <LayoutControl 
        layout={layout} 
        setLayout={setLayout} 
        onExportImage={handleExportImage}
        onExportPDF={handleExportPDF}
        exportLoading={exportLoading}
      />
      <div className="row w-100" ref={contentRef}>
        <div className={getLayoutClass(0)}>
          <ChartCard
            title={`Vacant Posts ${
              isNotGlobal
                ? `In ${user?.selectedEntity?.name}`
                : "across Districts"
            }`}
            subheader="Distribution of vacancies across districts"
          >
            <DistrictVacancyDistributionChart
              data={data?.districtVacancyDistributions}
              summary={data?.vacancyDistributionSummary}
            />
          </ChartCard>
        </div>
        <div className={getLayoutClass(1)}>
          <ChartCard
            title={`Vacancy Trends by Academic Year ${
              isNotGlobal ? `In ${user?.selectedEntity?.name}` : ""
            }`}
            subheader="Distribution of vacancies over academic years"
          >
            <AcademicYearVacancyDistributionChart
              data={data?.academicYearVacancyDistributions}
            />
          </ChartCard>
        </div>

        {user?.selectedScope?.id !== "RTB" && (
          <div className={getLayoutClass(2)}>
            <ChartCard
              title={`Vacancy Distribution by Teaching Level ${
                isNotGlobal ? `In ${user?.selectedEntity?.name}` : ""
              }`}
              subheader="Distribution of vacancies by teaching level"
            >
              <TeachingLevelVacanciesChart
                data={data?.teachingLevelVacancyDistributions}
              />
            </ChartCard>
          </div>
        )}

        <div className={getLayoutClass(3)}>
          <ChartCard
            title={`Vacancy Distribution by Qualification ${
              isNotGlobal ? `In ${user?.selectedEntity?.name}` : ""
            }`}
            subheader="Distribution of vacancies by qualification"
          >
            <QualificationVacanciesChart
              data={data?.qualificationVacancyDistributions}
            />
          </ChartCard>
        </div>

        <div className="col-12 p-0">
          <ChartCard
            title={`Vacancy Distribution by Teaching Level, Role and Qualification ${
              isNotGlobal ? `In ${user?.selectedEntity?.name}` : ""
            }`}
            subheader="Detailed breakdown of vacancies by teaching level, classification, and qualification"
            customActions={staffDistributionCustomActions}
            hideDefaultActions={true}
          >
            <VacancyDistributionTable
              data={data?.vacanciesByLevelRoleAndQualification}
              sortBy={vacancyDistributionSortBy}
              ref={vacancyDistributionTableRef}
            />
          </ChartCard>
        </div>

        <div className="col-12 p-0">
          <ChartCard
            title={`Vacancy Distribution by Position ${
              isNotGlobal ? `In ${user?.selectedEntity?.name}` : ""
            }`}
            subheader="Distribution of vacancies by position"
          >
            <PositionVacanciesChart data={data?.positionVacancyDistributions} />
          </ChartCard>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = ({ user, loading }) => {
  return { user, loading };
};

export default connect(mapStateToProps, {})(VacanciesOversight);
